import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { albumData } from '../data/albumData';
import { db, doc, setDoc, updateDoc, onSnapshot, collection, query, where, getDocs, writeBatch, getDoc, deleteDoc } from '../firebase';
import { useUI } from '../contexts/UIContext';
import { handleFirestoreWrite } from '../utils/firestoreErrors';
import { applyCompletedTrade } from '../utils/applyCompletedTrade';
import { reconcileProposalAfterTrade } from '../utils/proposalSync';
import { buildCompletedByPatch } from '../utils/markProposalCompleted';

const WRITE_DEBOUNCE_MS = 400;

const STORAGE_KEY = 'album-mundial-2026';

// Shallow equality for arrays of primitives. Used to skip no-op
// Firestore writes when the user re-selects the same set of stickers.
function arrayEq(a, b) {
  const aa = Array.isArray(a) ? a : [];
  const bb = Array.isArray(b) ? b : [];
  if (aa.length !== bb.length) return false;
  for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false;
  return true;
}

function getAllStickers() {
  const all = [];
  albumData.sections.forEach(section => {
    section.stickers.forEach(s => all.push({ ...s, section: section.id, sectionName: section.name }));
  });
  albumData.groups.forEach(group => {
    group.teams.forEach(team => {
      team.stickers.forEach(s => all.push({ ...s, group: group.id, groupName: group.name, team: team.code, teamName: team.name, teamFlag: team.flag }));
    });
  });
  return all;
}

function loadLocalState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) { console.error('Error loading state', e); }
  // Default shape MUST match the useState seed so anonymous-mode
  // users get the same set of keys as cloud-synced ones (missing
  // `trades` here caused state.trades to flicker undefined on
  // anonymous mounts).
  return { collected: {}, duplicates: {}, friends: {}, trades: {}, history: [] };
}

function saveLocalState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
  catch (e) { console.error('Error saving state', e); }
}

export function useAlbum(user) {
  const uid = user?.uid;
  // Initial state must include EVERY field downstream code reads
  // without optional chaining — otherwise the first render before the
  // onSnapshot resolves would throw on accesses like state.trades[fid].
  // (state.trades was missing here and used to crash if any code path
  // hit it during initial render.)
  const [state, setState] = useState({
    collected: {},
    duplicates: {},
    friends: {},
    trades: {},
    history: [],
  });
  const [loading, setLoading] = useState(true);
  const { showNotification, confirm } = useUI();
  const allStickers = useMemo(getAllStickers, []);

  // Espejo síncrono de `state`. Necesario para acciones imperativas
  // tipo completeTrade que necesitan computar nextState SIN esperar
  // el ciclo de render. Antes el código hacía:
  //
  //   let nextState = null;
  //   setState(prev => { ...; nextState = ...; return ...; });
  //   if (!nextState) return;  // ← se ejecutaba ANTES de que React
  //                                invocara el updater → nextState
  //                                seguía null → bailout silencioso,
  //                                Firestore nunca recibía el write.
  //
  // Con stateRef.current podemos calcular nextState inmediatamente.
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  // ───── Debounced write queue ─────
  // Coalesces rapid toggles into a single Firestore write to save costs.
  // Each entry is the LATEST value for that field; multiple toggles of the
  // same sticker collapse to one final state.
  const pendingFieldsRef = useRef({});   // e.g. { collected: {...}, duplicates: {...} }
  const flushTimerRef = useRef(null);

  const flushWrites = useCallback(() => {
    if (!uid) return;
    const fields = pendingFieldsRef.current;
    if (!fields || Object.keys(fields).length === 0) return;
    pendingFieldsRef.current = {};
    flushTimerRef.current = null;
    handleFirestoreWrite(
      () => updateDoc(doc(db, 'users', uid), fields),
      { op: 'flushWrites', fieldKeys: Object.keys(fields) }
    ).catch(err => console.error('Firestore write failed:', err));
  }, [uid]);

  const queueWrite = useCallback((fields) => {
    if (!uid) return;
    pendingFieldsRef.current = { ...pendingFieldsRef.current, ...fields };
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(flushWrites, WRITE_DEBOUNCE_MS);
  }, [uid, flushWrites]);

  // Flush any pending writes on unmount / before unload so we don't lose changes
  useEffect(() => {
    const onBeforeUnload = () => flushWrites();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      flushWrites();
    };
  }, [flushWrites]);

  // Sync with Firestore or LocalStorage
  useEffect(() => {
    if (uid) {
      setLoading(true);
      const unsub = onSnapshot(
        doc(db, 'users', uid),
        (docSnap) => {
          if (docSnap.exists()) {
            setState(prev => ({ ...prev, ...docSnap.data() }));
          } else {
            const local = loadLocalState();
            setState(local);
            handleFirestoreWrite(
              () => setDoc(doc(db, 'users', uid), local),
              { op: 'initializeUserDoc' }
            ).catch(() => { /* reported by handleFirestoreWrite */ });
          }
          setLoading(false);
        },
        (err) => {
          // Without this handler, an onSnapshot failure (permission,
          // quota, network) would leave loading=true forever and the
          // user would see "Cargando…" with no exit.
          console.error('Snapshot error:', err);
          setLoading(false);
        }
      );

      // Profile initialization: only run on FIRST sign-up so we don't
      // overwrite the user's custom name on every page load with their
      // Google displayName / email prefix.
      (async () => {
        try {
          const existing = await getDoc(doc(db, 'profiles', uid));
          if (!existing.exists()) {
            await handleFirestoreWrite(
              () => setDoc(doc(db, 'profiles', uid), {
                name: user.displayName || user.email.split('@')[0],
                photo: user.photoURL || '',
                lastSeen: Date.now()
              }, { merge: true }),
              { op: 'initializeProfile' }
            );
          }
        } catch (err) {
          console.error('Profile init failed:', err);
        }
      })();

      return () => unsub();
    } else {
      setState(loadLocalState());
      setLoading(false);
    }
  }, [uid]);

  const toggleSticker = useCallback((number, opts = {}) => {
    if (loading) return;
    let wasCollected = false;
    setState(prev => {
      wasCollected = !!prev.collected?.[number];
      const collected = { ...(prev.collected || {}) };
      if (collected[number]) { delete collected[number]; }
      else { collected[number] = true; }
      const newState = { ...prev, collected };

      if (uid) queueWrite({ collected });
      else saveLocalState(newState);

      return newState;
    });
    // Offer Undo (suppressed when an undo *is* the one doing the toggle)
    if (!opts.silent) {
      const action = wasCollected ? 'removida' : 'agregada';
      showNotification({
        message: `Lámina ${number} ${action}.`,
        type: 'success',
        action: {
          label: '↩ Deshacer',
          onClick: () => toggleSticker(number, { silent: true }),
        },
      });
    }
  }, [uid, loading, queueWrite, showNotification]);

  /**
   * Marks multiple stickers as collected in a single state update +
   * single Firestore write. Used by the onboarding "paste a list of
   * láminas you already have" flow.
   *
   * The previous flow ran toggleSticker() in a for-loop, which:
   *   - Fired one toast per sticker (50 láminas pasted = 50 toasts
   *     queueing for ~45s after onboarding completed)
   *   - Triggered the debounced write per call (less efficient than
   *     a single batch)
   *
   * markStickers is silent (no per-item toast, no achievement toasts
   * since the caller can decide what to show after a bulk import).
   */
  const markStickers = useCallback((numbers) => {
    if (!Array.isArray(numbers) || numbers.length === 0 || loading) return;
    setState(prev => {
      const collected = { ...(prev.collected || {}) };
      numbers.forEach(num => { collected[num] = true; });
      const newState = { ...prev, collected };
      if (uid) queueWrite({ collected });
      else saveLocalState(newState);
      return newState;
    });
  }, [uid, loading, queueWrite]);

  const getReservedCount = useCallback((number) => {
    // Normalize: strip whitespace + uppercase. Defends against historical
    // inconsistencies where the same sticker number is stored as e.g.
    // "SCO 12" in one trade and "SCO12" elsewhere. Both must match.
    const norm = (v) => String(v ?? '').replace(/\s+/g, '').toUpperCase();
    const target = norm(number);
    let count = 0;
    Object.values(state.trades || {}).forEach((t) => {
      if (!t || !t.reserved) return;
      const give = Array.isArray(t.give) ? t.give : [];
      if (give.some(g => norm(g) === target)) count++;
    });
    return count;
  }, [state.trades]);

  /**
   * Returns how many copies of a sticker are still available to offer
   * after subtracting the reserved ones across all pending trades.
   * If the user has 3 repeats and 2 are reserved for Pedro, this returns 1.
   */
  const getAvailableCount = useCallback((number) => {
    const total = state.duplicates?.[number] || 0;
    return Math.max(0, total - getReservedCount(number));
  }, [state.duplicates, getReservedCount]);

  const setDuplicates = useCallback((number, count) => {
    if (loading) return;
    const reserved = getReservedCount(number);
    if (count < reserved) {
      showNotification(`No puedes reducir esta lámina a ${count} porque tienes ${reserved} reservada(s) en intercambios pendientes.`, 'warning');
      return;
    }

    setState(prev => {
      const duplicates = { ...(prev.duplicates || {}) };
      if (count <= 0) { delete duplicates[number]; }
      else { duplicates[number] = count; }
      const newState = { ...prev, duplicates };

      if (uid) queueWrite({ duplicates });
      else saveLocalState(newState);

      return newState;
    });
  }, [uid, loading, getReservedCount, queueWrite]);

  const getProgress = useCallback((stickers) => {
    const total = stickers.length;
    const collected = state.collected || {};
    const have = stickers.filter(s => collected[s.number]).length;
    return { total, have, pct: total ? Math.round((have / total) * 100) : 0 };
  }, [state.collected]);

  const totalProgress = useMemo(() => getProgress(allStickers), [allStickers, getProgress]);

  const totalDuplicates = useMemo(() =>
    Object.values(state.duplicates || {}).reduce((a, b) => a + b, 0),
  [state.duplicates]);

  const exportData = useCallback(() => {
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'album-mundial-2026.json'; a.click();
    URL.revokeObjectURL(url);
  }, [state]);

  /**
   * Full GDPR-style data export: pulls everything Firestore has about
   * the user into a single JSON file. Includes:
   *   - state (album state — already in memory)
   *   - profile (public: name, photo, city, bio, lastSeen)
   *   - private profile (phone, fcmToken)
   *   - proposals (sent and received)
   * The download blob is JSON the user can keep / re-import / show to
   * data-rights regulators.
   */
  const exportAllMyData = useCallback(async () => {
    if (!uid) {
      // Anonymous local user — fallback to plain state export
      exportData();
      return;
    }
    try {
      const [publicSnap, privateSnap, sentSnap, receivedSnap] = await Promise.all([
        getDoc(doc(db, 'profiles', uid)),
        getDoc(doc(db, 'profiles_private', uid)),
        getDocs(query(collection(db, 'proposals'), where('fromUid', '==', uid))),
        getDocs(query(collection(db, 'proposals'), where('toUid', '==', uid))),
      ]);
      const payload = {
        exportedAt: new Date().toISOString(),
        uid,
        album: state,
        publicProfile: publicSnap.exists() ? publicSnap.data() : null,
        privateProfile: privateSnap.exists() ? privateSnap.data() : null,
        sentProposals: sentSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        receivedProposals: receivedSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mis-datos-album-2026-${uid.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showNotification('Datos exportados. Revisa tu carpeta de descargas.', 'success');
    } catch (err) {
      console.error('Export failed:', err);
      showNotification('No pudimos exportar tus datos. Inténtalo de nuevo.', 'error');
    }
  }, [uid, state, exportData, showNotification]);

  /**
   * Wipes everything the user has on Firestore. Caller is responsible
   * for then calling Firebase Auth deleteUser() — we can't do that
   * here because we don't have the auth instance.
   *
   * Returns a Promise that resolves with the number of docs/proposals
   * deleted on success, or rejects on failure.
   */
  const deleteMyAccount = useCallback(async () => {
    if (!uid) throw new Error('No hay sesión activa');
    // Find every proposal the user is part of (in either direction) so
    // we can clean them up — they may be visible to the other party.
    const [sentSnap, receivedSnap] = await Promise.all([
      getDocs(query(collection(db, 'proposals'), where('fromUid', '==', uid))),
      getDocs(query(collection(db, 'proposals'), where('toUid', '==', uid))),
    ]);
    const proposalDeletes = [
      ...sentSnap.docs.map(d => deleteDoc(d.ref)),
      ...receivedSnap.docs.map(d => deleteDoc(d.ref)),
    ];
    await Promise.all(proposalDeletes);
    // Wipe the three user-owned docs.
    await Promise.all([
      deleteDoc(doc(db, 'users', uid)),
      deleteDoc(doc(db, 'profiles', uid)),
      deleteDoc(doc(db, 'profiles_private', uid)),
    ]);
    return { proposalsDeleted: proposalDeletes.length };
  }, [uid]);

  const importData = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (data.collected) {
          setState(data);
          if (uid) setDoc(doc(db, 'users', uid), data);
          else saveLocalState(data);
          showNotification('Datos importados correctamente', 'success');
        }
      } catch (err) { showNotification('Archivo inválido', 'error'); }
    };
    reader.readAsText(file);
  }, [uid]);

  const resetAll = useCallback(async () => {
    if (await confirm('¿Estás seguro de borrar todo tu progreso?')) {
      // Full shape so React reads of state.friends / .trades / .history
      // don't return undefined after reset (they'd be {} or [] from the
      // initial useState seed, but after setDoc with the partial object
      // and then a snapshot, the local state would lose those keys).
      const emptyState = { collected: {}, duplicates: {}, friends: {}, trades: {}, history: [] };
      setState(emptyState);
      if (uid) {
        try {
          await handleFirestoreWrite(
            () => setDoc(doc(db, 'users', uid), emptyState),
            { op: 'resetAll' }
          );
        } catch (err) {
          console.error('Reset failed:', err);
          showNotification('No pudimos limpiar tus datos en la nube. Inténtalo de nuevo.', 'error');
          return;
        }
      } else {
        saveLocalState(emptyState);
      }
      showNotification('Progreso borrado', 'success');
    }
  }, [uid, confirm, showNotification]);

  const setFriends = useCallback((friends) => {
    setState(prev => {
      const newState = { ...prev, friends };
      if (uid) setDoc(doc(db, 'users', uid), newState);
      else saveLocalState(newState);
      return newState;
    });
  }, [uid]);

  const addFriend = useCallback((friendUid, friendName) => {
    setState(prev => {
      if (prev.friends?.[friendUid]) return prev; // idempotent — no duplicate writes
      const friends = { ...(prev.friends || {}) };
      friends[friendUid] = { name: friendName, addedAt: new Date().toISOString() };
      const newState = { ...prev, friends };
      // IMMEDIATE write (not queueWrite) — adding a friend right before
      // an F5 (typical for the "pegar texto → reservar → refrescar"
      // flow with external friends) would otherwise lose the friend
      // because the 400ms debounce hasn't fired. Also flush any pending
      // writes so they go out before this one.
      if (uid) {
        if (Object.keys(pendingFieldsRef.current).length > 0) flushWrites();
        handleFirestoreWrite(
          () => updateDoc(doc(db, 'users', uid), { friends }),
          { op: 'addFriend' }
        ).catch(err => console.error('addFriend write failed:', err));
      } else {
        saveLocalState(newState);
      }
      return newState;
    });
  }, [uid, flushWrites]);

  const removeFriend = useCallback((friendUid) => {
    setState(prev => {
      if (!prev.friends?.[friendUid] && !prev.trades?.[friendUid]) return prev;
      const friends = { ...(prev.friends || {}) };
      delete friends[friendUid];
      const trades = { ...(prev.trades || {}) };
      delete trades[friendUid];
      const newState = { ...prev, friends, trades };
      if (uid) {
        if (Object.keys(pendingFieldsRef.current).length > 0) flushWrites();
        handleFirestoreWrite(
          () => updateDoc(doc(db, 'users', uid), { friends, trades }),
          { op: 'removeFriend' }
        ).catch(err => console.error('removeFriend write failed:', err));
      } else {
        saveLocalState(newState);
      }
      return newState;
    });
  }, [uid, flushWrites]);

  const updateProfile = useCallback(async (data) => {
    if (!uid) return;
    // Separar campos públicos de privados.
    // phone NUNCA va a profiles/ (es público); va a profiles_private/.
    const { phone, ...publicData } = data || {};
    // Writes are sequential, not Promise.all, to avoid the case where
    // one half succeeds and the other fails — leaving the profile in
    // an inconsistent state where (say) name was updated but phone
    // wasn't. Both wrapped in handleFirestoreWrite so quota errors
    // surface to the global banner + Sentry.
    await handleFirestoreWrite(
      () => setDoc(doc(db, 'profiles', uid), {
        ...publicData,
        // Date.now() (number) not toISOString() (string). The
        // activity-indicator math in Community/PublicProfile is
        // `Date.now() - lastSeen`, which produces NaN if lastSeen is
        // a string — and NaN silently makes the indicator disappear.
        lastSeen: Date.now()
      }, { merge: true }),
      { op: 'updateProfilePublic' }
    );
    if (phone !== undefined) {
      await handleFirestoreWrite(
        () => setDoc(doc(db, 'profiles_private', uid), { phone }, { merge: true }),
        { op: 'updateProfilePrivate' }
      );
    }
  }, [uid]);

  const setTrade = useCallback((friendUid, tradeData, opts = {}) => {
    if (loading) return;
    setState(prev => {
      const prevTrade = prev.trades?.[friendUid];
      // Skip write entirely if nothing meaningful changed. Without this
      // check, the autosave effect in TradeMatch would re-fire on every
      // onSnapshot tick (because myState.trades is a new reference) and
      // re-write the SAME data — each click was costing 2–3 writes
      // instead of 1.
      const sameReceive = arrayEq(prevTrade?.receive, tradeData.receive);
      const sameGive    = arrayEq(prevTrade?.give,    tradeData.give);
      const sameRes     = !!prevTrade?.reserved === !!tradeData.reserved;
      const sameNote    = (prevTrade?.note || '') === (tradeData.note || '');
      if (prevTrade && sameReceive && sameGive && sameRes && sameNote) {
        return prev; // no change → no write
      }
      const trades = { ...(prev.trades || {}) };
      trades[friendUid] = { ...tradeData, updatedAt: new Date().toISOString() };
      const newState = { ...prev, trades };
      // Route through the debounced queue so 50 sticker clicks in a row
      // collapse to a single Firestore write instead of 50.
      // EXCEPTION: when `reserved` flips, write IMMEDIATELY. Reserving
      // is a critical user action ("estas láminas son tuyas") and users
      // often F5 right after — the 400ms debounce would lose the flag.
      // Force immediate write when:
      //   - `reserved` flips (critical user action they F5 right after)
      //   - caller passed { immediate: true } (Optimizar / Auto-Equilibrar
      //     are "save my work NOW" user actions where the 400ms debounce
      //     would silently lose the changes on a fast F5).
      const reservedChanged = !!prevTrade?.reserved !== !!tradeData.reserved;
      const writeNow = reservedChanged || opts.immediate;
      if (uid) {
        if (writeNow) {
          if (Object.keys(pendingFieldsRef.current).length > 0) flushWrites();
          handleFirestoreWrite(
            () => updateDoc(doc(db, 'users', uid), { trades }),
            { op: reservedChanged ? 'setTrade.reservedFlip' : 'setTrade.immediate', friendUid }
          ).catch(err => console.error('setTrade write failed:', err));
        } else {
          queueWrite({ trades });
        }
      } else {
        saveLocalState(newState);
      }
      return newState;
    });
  }, [uid, loading, queueWrite, flushWrites]);

  const removeTrade = useCallback((friendUid) => {
    if (loading) return;
    setState(prev => {
      if (!prev.trades?.[friendUid]) return prev; // already gone
      const trades = { ...(prev.trades || {}) };
      delete trades[friendUid];
      const newState = { ...prev, trades };
      if (uid) queueWrite({ trades });
      else saveLocalState(newState);
      return newState;
    });
  }, [uid, loading, queueWrite]);

  /**
   * Frees every reserved trade at once: keeps the trade records (so
   * the conversation history with each friend isn't lost), but flips
   * reserved:false everywhere. Returns the number of trades that were
   * actually unflipped.
   *
   * Use case: user got stuck with reservations from old/abandoned
   * trades and wants to "reclaim" their repetidas without manually
   * opening each trade.
   */
  const clearAllReservations = useCallback(() => {
    if (loading) return 0;
    let cleared = 0;
    setState(prev => {
      const trades = { ...(prev.trades || {}) };
      Object.keys(trades).forEach(fid => {
        if (trades[fid]?.reserved) {
          trades[fid] = { ...trades[fid], reserved: false };
          cleared++;
        }
      });
      if (cleared === 0) return prev;
      const newState = { ...prev, trades };
      if (uid) {
        handleFirestoreWrite(
          () => updateDoc(doc(db, 'users', uid), { trades }),
          { op: 'clearAllReservations' }
        ).catch(err => console.error('clearAllReservations failed:', err));
      } else {
        saveLocalState(newState);
      }
      return newState;
    });
    return cleared;
  }, [uid, loading]);

  const completeTrade = useCallback(async (receiveNumbers, giveNumbers, friendUid, friendNameOverride, proposalId) => {
    if (loading) return;

    // Calculamos nextState SÍNCRONO desde stateRef.current — antes
    // este código intentaba capturar nextState desde dentro del
    // updater de setState (que React invoca durante render, no en
    // el momento de la llamada), por lo que `nextState` quedaba null
    // y el código entero abortaba antes de escribir a Firestore.
    //
    // friendNameOverride: opcional. Si se pasa, se usa para el history
    // sin depender de que el friendUid esté en state.friends. Usado
    // por el flujo "Intercambio presencial" para evitar tener que
    // crear un ext-friend ephemero que se borraría justo después.
    const { nextState, finalDuplicates } = applyCompletedTrade(
      stateRef.current,
      receiveNumbers,
      giveNumbers,
      friendUid,
      friendNameOverride,
    );

    // Aplicar el nuevo estado a React sin update function (ya es
    // un objeto final). React acepta esta forma cuando el cómputo
    // depende del estado anterior pero ya lo capturamos vía el ref.
    setState(nextState);

    if (uid) {
      try {
        await handleFirestoreWrite(
          () => updateDoc(doc(db, 'users', uid), {
            collected: nextState.collected,
            duplicates: nextState.duplicates,
            trades: nextState.trades,
            history: nextState.history
          }),
          { op: 'completeTrade', friendUid }
        );
      } catch (err) {
        console.error("Error persisting trade:", err);
        // Local state already updated optimistically. Banner already shown
        // by handleFirestoreWrite. We still continue so the user sees the
        // success toast — Firestore SDK will retry from IndexedDB.
      }
    } else {
      saveLocalState(nextState);
    }

    showNotification(`¡Intercambio procesado! (${receiveNumbers.length} recibidas, ${giveNumbers.length} entregadas)`, 'success');

    // 4. Update Pending Proposals Automatically
    if (!uid) return;
    
    try {
      const qFrom = query(collection(db, 'proposals'), where('fromUid', '==', uid), where('status', '==', 'pending'));
      const qTo = query(collection(db, 'proposals'), where('toUid', '==', uid), where('status', '==', 'pending'));
      
      const [snapFrom, snapTo] = await Promise.all([getDocs(qFrom), getDocs(qTo)]);
      const allDocs = [...snapFrom.docs, ...snapTo.docs];

      let updatedCount = 0;
      const myDisplayName = friendNameOverride || nextState.profile?.name || 'El otro coleccionista';

      for (const docSnap of allDocs) {
        const prop = docSnap.data();

        // CASO ESPECIAL: si este es el proposal que el usuario está
        // CONFIRMANDO explícitamente (pasamos su id como argumento),
        // NO le aplicamos el reconcile genérico — eso pondría status
        // 'cancelled' y dejaría al otro lado con la impresión de que
        // se canceló el trato. En su lugar, agregamos mi uid al array
        // `completedBy`. El doc queda 'pending' hasta que la otra
        // parte también confirme — modelo bilateral testeado en
        // markProposalCompleted.test.js.
        if (proposalId && docSnap.id === proposalId) {
          const patch = buildCompletedByPatch(prop, uid, myDisplayName);
          await updateDoc(docSnap.ref, patch);
          updatedCount++;
          continue;
        }

        // Resto de propuestas pendientes: aplica el reconcile clásico
        // — si esta otra propuesta involucraba láminas que acabamos
        // de intercambiar, se retiran de receive/give con su lista
        // detallada en systemMessage.
        const result = reconcileProposalAfterTrade(
          prop,
          uid,
          nextState.collected,
          finalDuplicates,
        );
        if (!result) continue;
        await updateDoc(docSnap.ref, result.updateObj);
        updatedCount++;
      }

      if (updatedCount > 0) {
          showNotification(`Se actualizaron automáticamente ${updatedCount} propuestas pendientes afectadas por este trato.`, 'info');
      }
      
    } catch (err) {
      console.error("Error updating proposals:", err);
    }
  }, [uid, loading, showNotification]);

  const rectifyInventory = useCallback((numbers) => {
    if (loading) return;
    setState(prev => {
      const duplicates = { ...(prev.duplicates || {}) };
      let count = 0;

      numbers.forEach(num => {
        const current = duplicates[num] || 0;
        if (current > 0) {
          count++;
          if (current > 1) {
            duplicates[num] = current - 1;
          } else {
            delete duplicates[num];
          }
        }
      });

      if (count === 0) {
        showNotification('No se encontraron repetidas para las láminas ingresadas.', 'warning');
        return prev;
      }

      const newState = { ...prev, duplicates };
      if (uid) updateDoc(doc(db, 'users', uid), { duplicates: newState.duplicates });
      else saveLocalState(newState);
      
      showNotification(`¡Rectificación completada! Se descontaron ${count} repetidas.`, 'success');
      return newState;
    });
  }, [uid, loading, showNotification]);

  const syncAllDuplicates = useCallback((newDuplicates) => {
    if (loading) return;
    setState(prev => {
      const newState = { ...prev, duplicates: newDuplicates };
      if (uid) updateDoc(doc(db, 'users', uid), { duplicates: newDuplicates });
      else saveLocalState(newState);
      showNotification('Inventario sincronizado correctamente.', 'success');
      return newState;
    });
  }, [uid, loading, showNotification]);

  return {
    state, allStickers, albumData, loading,
    toggleSticker, markStickers, setDuplicates,
    getProgress, totalProgress, totalDuplicates,
    exportData, exportAllMyData, importData, resetAll, deleteMyAccount,
    addFriend, removeFriend, setFriends,
    updateProfile, setTrade, removeTrade, clearAllReservations,
    getReservedCount, getAvailableCount,
    completeTrade, rectifyInventory, syncAllDuplicates
  };
}
